/**
 * The P5 balance check (ADR-0028): assets = Σ booksUsd over ASSET_BOOKS + asset statics (every StaticItemId except other_liabilities)
 * − aclUsd; liabilities = Σ booksUsd over LIABILITY_BOOKS + other_liabilities; equity = the four Equity fields. `swap_overlay` is an
 * asset when ≥ 0 and a liability when < 0. Tolerance ±$1.
 */
import { ASSET_BOOKS, LIABILITY_BOOKS, type StaticItemId } from "@bsim/domain";
import type { RoundReport } from "@bsim/contracts";

export type Statements = RoundReport["quarterly"][number]["statements"];

export const BALANCE_TOLERANCE_USD = 1;

const sum = (values: readonly (number | undefined)[]): number => values.reduce<number>((total, v) => total + (v ?? 0), 0);

const ASSET_STATICS: readonly StaticItemId[] = ["premises", "goodwill", "intangibles_cdi", "boli", "dta", "other_assets"];

export interface BalanceTotals { readonly assetsUsd: number; readonly liabilitiesUsd: number; readonly equityUsd: number }

export function balanceTotals(s: Statements): BalanceTotals {
  const { booksUsd, staticItemsUsd, aclUsd, equity } = s.balanceSheet;
  const swap = booksUsd.swap_overlay ?? 0;
  return {
    assetsUsd: sum(ASSET_BOOKS.map((b) => booksUsd[b])) + sum(ASSET_STATICS.map((k) => staticItemsUsd[k])) - aclUsd + Math.max(swap, 0),
    liabilitiesUsd: sum(LIABILITY_BOOKS.map((b) => booksUsd[b])) + (staticItemsUsd.other_liabilities ?? 0) + Math.max(-swap, 0),
    equityUsd: equity.commonApicUsd + equity.retainedEarningsUsd + equity.aociAfsUsd + equity.aociSwapUsd,
  };
}

/** How far assets are from liabilities + equity, in whole dollars. */
export function balanceGapUsd(s: Statements): number {
  const t = balanceTotals(s);
  return t.assetsUsd - (t.liabilitiesUsd + t.equityUsd);
}

export const balances = (s: Statements): boolean => Math.abs(balanceGapUsd(s)) <= BALANCE_TOLERANCE_USD;
